import React, { type FC } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';

import { Overview } from '../ActivityDetailsTabs/Overview/Overview';
import { ClinicalRecordsTab } from '../ActivityDetailsTabs/ClinicalRecords';
import { ClientDetails } from '../ActivityDetailsTabs/ClientDetails/ClientDetails';
import { Requests } from '../ActivityDetailsTabs/Requests/Requests';
import { InvoicesTab } from '../ActivityDetailsTabs/Invoices/InvoicesTab';
import { type IActivityDetails, type IFormByActivity } from '../../../interfaces';
import { useAuth } from 'components/Auth';
import { type StepDefinition } from 'components/ToDos';
import { Button } from 'components/Shared/Inputs';
import { useScreenSize } from 'components/Layout';
import { type IContractDetails } from 'hooks/useContractDetails/interfaces';
import { useFeatureManager } from 'hooks/useFeatureManager';
import { type OverHoursApprovalDTO } from 'components/Requests/interfaces';
// import { AssessmentTools } from '../ActivityDetailsTabs/AssessmentTools';
import { PROVIDER_HUB } from 'constants/targetedSystem';
import InvoiceDraftProvider from 'components/Invoicing/components/InvoiceDraftProvider';
import { EmergencyContactTab } from '../ActivityDetailsTabs/EmergencyContact/EmergencyContactTab';
import { type LazyQueryExecFunction } from '@apollo/client';
import { type ActivityQueryData } from '../../queries';
import { useIsEmergencyContactEnabled } from 'hooks/useIsEmergencyContactEnabled';

export interface ActivityDetailsContentProps {
    activity: IActivityDetails,
    refetchActivityDetails: LazyQueryExecFunction<ActivityQueryData, { activityId: number; }>,
    forms?: IFormByActivity[],
    step: StepDefinition | null,
    onShowDetailsClick: () => void
    contractDetails: IContractDetails | undefined,
    onAdditionalHoursRequestCreated: () => void,
    additionalHoursApprovals: OverHoursApprovalDTO[] | undefined
}

export const ActivityDetailsContent: FC<ActivityDetailsContentProps> = (props) => {
    const { activity, onShowDetailsClick, forms, step,
        additionalHoursApprovals, onAdditionalHoursRequestCreated, refetchActivityDetails } = props;

    const { t } = useTranslation('activitydetails', { useSuspense: false });
    const { isAdditionalHoursFeatureEnabled } = useFeatureManager();
    const location = useLocation();
    const { effectiveProviderData } = useAuth();
    const { isMobile } = useScreenSize();
    const fromToDo = location?.state?.["fromToDo"];
    const activityPath = `/clients/${props.activity?.activityId}`;

    const showRequestsTab = isAdditionalHoursFeatureEnabled()
        && (activity?.contractAllowsAdditionalHours || additionalHoursApprovals?.some(approval => approval.request));
    const isNewActivity = useIsEmergencyContactEnabled(activity);

    function getCurrentTabValue() {
        const match = location.pathname.replace(/\/clients\/[0-9]+\//, "") || activityPath;
        return match?.startsWith('invoices') ? "invoices" : match;
    }

    return (
        <div className="activity_details_content_wrapper">
            {
                isMobile &&
                <section className="client_name_and_details_button_section">
                    <h1 className="global__header1 client_name">
                        {`${activity?.client?.firstName} ${activity?.client?.lastName}`}
                    </h1>
                    <Button variant="text" onClick={onShowDetailsClick} className="client_details_button">
                        Details
                    </Button>
                </section>
            }
            <Tabs className="activity_details_tabs" indicatorColor="primary"
                variant="scrollable"
                scrollButtons={false}
                value={getCurrentTabValue()}
            >
                {
                    activity?.isAvailableToProvider &&
                    <Tab className="button_tab" component={Link} value={activityPath} label={t('overview', 'Overview')} to={activityPath} />
                }
                {
                    activity?.isAvailableToProvider && activity?.targetedSystem?.systemCode === PROVIDER_HUB &&
                    <Tab className="button_tab" component={Link} value="documents" label={t('clinical_records', 'Clinical Records')} to={`${activityPath}/documents`} />
                }
                {/* {
                    activity?.isAvailableToProvider && activity?.targetedSystem?.systemCode === PROVIDER_HUB &&
                    <Tab className="button_tab" component={Link} value="/assessment_tools" label={t('assessment_tools')} to={`/clients/${props.activity?.activityId}/assessment_tools`} />
                }   */}
                {
                     activity?.isAvailableToProvider &&
                     <Tab className="button_tab" component={Link} value="details" label={t('client_details', 'Client Details')} to={`${activityPath}/details`} />
                }
                {
                    activity?.isAvailableToProvider && isNewActivity &&
                    <Tab className="button_tab" component={Link} value="emergency_contact" label={t('emergency_contact')} to={`${activityPath}/emergency_contact`} />
                }       
                {
                    effectiveProviderData?.hasInvoicing &&
                    <Tab className="button_tab" component={Link} value="invoices" label={t('invoices', 'Invoices')} to={`${activityPath}/invoices`} />
                }
                {
                    showRequestsTab && activity?.isAvailableToProvider &&
                    <Tab className="button_tab" component={Link} value="requests" label={t('requests', 'Requests')} to={`${activityPath}/requests`} />
                }
            </Tabs>
            <Routes>
                <Route 
                    path="/"
                    element={
                        <Overview activity={activity} step={step} contractDetails={props.contractDetails} onAdditionalHoursRequestCreated={onAdditionalHoursRequestCreated} />
                    }
                />
                <Route
                    path="/documents"
                    element={
                        <ClinicalRecordsTab activity={activity} forms={forms} preAssessments={activity?.preAssessments} fromToDo={fromToDo}/>
                    }
                />
                {/* <Route 
                        path={`${path}/assessment_tools`}
                        element={<AssessmentTools activityId={activity?.activityId} forms={forms} sessions={activity.sessions}/>}
                /> */}
                <Route
                    path="/details"
                    element={<ClientDetails activity={activity} refetchActivity={refetchActivityDetails} />}
                />
                <Route
                    path="/emergency_contact"
                    element={<EmergencyContactTab activity={activity} refetchActivity={refetchActivityDetails} />}
                />
                <Route
                    path="/requests"
                    element={<Requests activity={activity} approvals={additionalHoursApprovals} onAdditionalHoursRequestCreated={onAdditionalHoursRequestCreated} />}
                />
                {
                    effectiveProviderData?.hasInvoicing
                    ? [
                        <Route
                            path="/invoices/:invoiceId"
                            key="invoices/:invoiceId"
                            element={
                                <InvoiceDraftProvider activityId={activity.activityId}>
                                    <InvoicesTab/>
                                </InvoiceDraftProvider>
                            }
                        />,
                        <Route
                            path="/invoices"
                            key="invoices"
                            element={<InvoicesTab/>}
                        />
                    ]
                    : <Route path="/invoices/*" element={<Navigate replace to="/" />} />
                }
            </Routes>
        </div>
    )
}